import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';

type Notification = {
  id: number;
  vendor: number;
  message: string;
  context_data: unknown;
  is_read: boolean;
  is_recent_notification: boolean;
  readed_at: string;
  created_at: string;
};

export interface NotificationsResponse {
  Order?: Notification[];
  Inventory?: Notification[];
  Payment?: Notification[];
  Promotion?: Notification[];
}

const getNotifications = (): Promise<NotificationsResponse> => {
  return AxiosInstance.get(api.notifications.list).then((res) => res.data);
};

const useGetNotifications = () => {
  return useQuery<NotificationsResponse>({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

const patchLatestNotification = async (category: string) => {
  const response = await AxiosInstance.patch(api.notifications.patch_latest_notification, {
    category,
  });
  return response.data;
};

const usePatchLatestNotification = () => {
  return useMutation({
    mutationKey: 'patch_latest_notification',
    mutationFn: patchLatestNotification,
  });
};

const markAllAsRead = () => {
  return AxiosInstance.patch(api.notifications.mark_as_all_read);
};

const useMarAsAllRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'mark_as_all_read',
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.setQueryData<NotificationsResponse>(['notifications'], (old) => {
        if (!old) {
          return {
            Order: [],
            Inventory: [],
            Payment: [],
            Promotion: [],
          };
        }

        const updated = { ...old };
        (Object.keys(updated) as Array<keyof NotificationsResponse>).forEach((cat) => {
          if (updated[cat]) {
            updated[cat] = updated[cat]!.map((n) => ({ ...n, is_read: true }));
          }
        });
        return updated;
      });
    },
  });
};

export { useGetNotifications, usePatchLatestNotification, useMarAsAllRead };
